import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import settlementService from '../services/settlementService';
import masterService from '../services/masterService';
import employeeService from '../services/employeeService';
import permissionService from '../services/permissionService';
import './CancellationPolicyPage.css';

/**
 * Admin-facing configuration for the test-cancellation authority engine
 * (centre_cancellation_policy — see 55_test_cancellation_policy_engine.sql).
 *
 * Every lab's own rule — "cancellation allowed only up to X stage, beyond
 * that Y approves" — is set here, as data. No code change is ever needed to
 * support a different lab's business logic; see SettlementPage's Cancel
 * Test modal for where this policy is actually enforced.
 */
export const CancellationPolicyPage = () => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canEdit = can('CANCELLATION_POLICY', 'UPDATE');
  const canManageAccess = can('ROLES', 'VIEW'); // Roles & Permissions matrix API is Admin-only server-side

  const [centres, setCentres] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);

  const [selectedCentreId, setSelectedCentreId] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [maxStage, setMaxStage] = useState(60);
  const [approverMode, setApproverMode] = useState('none'); // none | role | employee
  const [approverRoleId, setApproverRoleId] = useState('');
  const [approverEmployeeId, setApproverEmployeeId] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // "Who Has Access" summary — live from Roles & Permissions (RBAC).
  // Only fetched for users who can actually open that screen (Admin-only server-side).
  const [accessLoading, setAccessLoading] = useState(false);
  const [policyAccess, setPolicyAccess] = useState([]);
  const [approvalsAccess, setApprovalsAccess] = useState([]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Load lookups once
  useEffect(() => {
    (async () => {
      try {
        const [centresRes, rolesRes, employeesRes, stagesRes] = await Promise.all([
          masterService.getCentres({ isActive: true }),
          employeeService.getRolesLookup(),
          employeeService.getEmployees({ isActive: true, page: 1, pageSize: 500 }),
          settlementService.getCancellationStageOptions(),
        ]);
        if (centresRes.success) setCentres(centresRes.data || []);
        if (rolesRes.success) setRoles(rolesRes.data || []);
        if (employeesRes.success) setEmployees(employeesRes.data?.items || []);
        if (stagesRes.success) setStageOptions(stagesRes.data || []);

        const firstCentreId = centresRes.data?.[0]?.centreId;
        if (firstCentreId) setSelectedCentreId(firstCentreId);
      } catch (err) {
        console.error('Error loading cancellation policy lookups:', err);
        showToast('Failed to load centres / roles / employees.', 'error');
      }
    })();
  }, []);

  const loadPolicy = useCallback(async (centreId) => {
    if (!centreId) return;
    setLoading(true);
    try {
      const res = await settlementService.getCancellationPolicy(centreId);
      if (res.success && res.data) {
        const p = res.data;
        setPolicy(p);
        setMaxStage(p.maxSelfServiceStage);
        if (p.approverEmployeeId) {
          setApproverMode('employee');
          setApproverEmployeeId(String(p.approverEmployeeId));
          setApproverRoleId('');
        } else if (p.approverRoleId) {
          setApproverMode('role');
          setApproverRoleId(String(p.approverRoleId));
          setApproverEmployeeId('');
        } else {
          setApproverMode('none');
          setApproverRoleId('');
          setApproverEmployeeId('');
        }
      }
    } catch (err) {
      console.error('Error loading cancellation policy:', err);
      showToast('Failed to load the cancellation policy for this centre.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCentreId) loadPolicy(selectedCentreId);
  }, [selectedCentreId, loadPolicy]);

  // Live "who has access" summary — one role-permission fetch per role, filtered
  // down to our two screen codes. Skipped entirely for non-Admin viewers since
  // the underlying API (/permissions/roles/{id}) is Admin-only server-side.
  useEffect(() => {
    if (!canManageAccess || roles.length === 0) return;
    let cancelled = false;
    (async () => {
      setAccessLoading(true);
      try {
        const results = await Promise.all(roles.map((r) => permissionService.getRolePermissions(r.id)));
        if (cancelled) return;
        const policyRows = [];
        const approvalRows = [];
        results.forEach((res, idx) => {
          const role = roles[idx];
          const perms = res.success ? (res.data?.permissions || []) : [];
          const policyGrants = perms.filter((p) => p.screenCode === 'CANCELLATION_POLICY' && p.isAllowed);
          const approvalGrants = perms.filter((p) => p.screenCode === 'CANCELLATION_APPROVALS' && p.isAllowed);
          if (policyGrants.length > 0) {
            policyRows.push({
              roleId: role.id,
              roleName: role.name,
              view: policyGrants.some((p) => p.actionCode === 'VIEW'),
              update: policyGrants.some((p) => p.actionCode === 'UPDATE'),
            });
          }
          if (approvalGrants.length > 0) {
            approvalRows.push({
              roleId: role.id,
              roleName: role.name,
              view: approvalGrants.some((p) => p.actionCode === 'VIEW'),
              approve: approvalGrants.some((p) => p.actionCode === 'APPROVE'),
            });
          }
        });
        setPolicyAccess(policyRows);
        setApprovalsAccess(approvalRows);
      } catch (err) {
        console.error('Error loading access summary:', err);
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roles, canManageAccess]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedCentreId) return;

    setSaving(true);
    try {
      const res = await settlementService.saveCancellationPolicy({
        centreId: selectedCentreId,
        maxSelfServiceStage: Number(maxStage),
        approverRoleId: approverMode === 'role' && approverRoleId ? Number(approverRoleId) : null,
        approverEmployeeId: approverMode === 'employee' && approverEmployeeId ? Number(approverEmployeeId) : null,
      });
      if (res.success && res.data) {
        setPolicy(res.data);
        showToast('Cancellation policy saved. It applies immediately — no restart or code change needed.', 'success');
      } else {
        showToast(res.message || 'Failed to save policy.', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Error saving cancellation policy.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const isGlobalRow = policy && !policy.hasCentreSpecificPolicy;

  return (
    <div className="cxp-page">
      {toast && <div className={`cxp-toast cxp-toast-${toast.type}`}>{toast.message}</div>}

      <div className="cxp-header">
        <div>
          <h1>🔒 Test Cancellation Policy</h1>
          <p className="cxp-subtitle">
            Decide, per centre, how far a test can be cancelled without approval — and who approves beyond that.
            This is pure configuration: every lab's own rule, no code change required.
          </p>
        </div>
        {canManageAccess && (
          <button type="button" className="cxp-btn-manage-access" onClick={() => navigate('/roles')}>
            🔐 Manage Access
          </button>
        )}
      </div>

      <div className="cxp-centre-select">
        <label>Centre</label>
        <select
          value={selectedCentreId || ''}
          onChange={(e) => setSelectedCentreId(Number(e.target.value))}
        >
          {centres.map((c) => (
            <option key={c.centreId} value={c.centreId}>{c.centreName}</option>
          ))}
        </select>
        {isGlobalRow && (
          <span className="cxp-badge-global">No policy set for this centre — showing the platform default.</span>
        )}
      </div>

      {loading ? (
        <div className="cxp-loading">Loading policy…</div>
      ) : (
        <form className="cxp-form" onSubmit={handleSave}>
          <div className="cxp-section">
            <h3>1. Self-Service Ceiling</h3>
            <p className="cxp-help">
              Any staff member with cancellation access can cancel a test directly up to this stage.
              Beyond it, cancelling sends an approval request instead of cancelling anything.
            </p>
            <div className="cxp-stage-options">
              {stageOptions.map((opt) => (
                <label key={opt.value} className={`cxp-stage-opt ${Number(maxStage) === opt.value ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="maxStage"
                    value={opt.value}
                    checked={Number(maxStage) === opt.value}
                    onChange={() => setMaxStage(opt.value)}
                    disabled={!canEdit}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cxp-section">
            <h3>2. Who Approves Beyond That</h3>
            <p className="cxp-help">
              The platform Administrator can always approve, at any stage, for every centre — no setup needed.
              Optionally, name this centre's own approver below so staff don't have to wait on Admin.
            </p>
            <div className="cxp-approver-row">
              <label className="cxp-radio">
                <input type="radio" checked={approverMode === 'none'} onChange={() => setApproverMode('none')} disabled={!canEdit} />
                Administrator only
              </label>
              <label className="cxp-radio">
                <input type="radio" checked={approverMode === 'role'} onChange={() => setApproverMode('role')} disabled={!canEdit} />
                Any employee with this role
              </label>
              <label className="cxp-radio">
                <input type="radio" checked={approverMode === 'employee'} onChange={() => setApproverMode('employee')} disabled={!canEdit} />
                One specific employee
              </label>
            </div>

            {approverMode === 'role' && (
              <select value={approverRoleId} onChange={(e) => setApproverRoleId(e.target.value)} disabled={!canEdit} className="cxp-select">
                <option value="">Select a role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}

            {approverMode === 'employee' && (
              <select value={approverEmployeeId} onChange={(e) => setApproverEmployeeId(e.target.value)} disabled={!canEdit} className="cxp-select">
                <option value="">Select an employee…</option>
                {employees.map((e) => (
                  <option key={e.employeeId} value={e.employeeId}>{e.name}</option>
                ))}
              </select>
            )}
          </div>

          {!canEdit && (
            <div className="cxp-readonly-note">
              You have view-only access. Only an Administrator, or this centre's own Centre Admin, can change this policy.
            </div>
          )}

          <div className="cxp-actions">
            <button type="submit" className="cxp-btn-save" disabled={!canEdit || saving}>
              {saving ? 'Saving…' : '💾 Save Policy'}
            </button>
          </div>
        </form>
      )}

      {canManageAccess && (
        <div className="cxp-access-panel">
          <h3>3. Who Has Access to This</h3>
          <p className="cxp-help">
            Live from Roles &amp; Permissions. The platform Administrator always has full access regardless of
            this list. Use <strong>Manage Access</strong> above to change any of these grants.
          </p>
          {accessLoading ? (
            <div className="cxp-loading-inline">Loading access…</div>
          ) : (
            <div className="cxp-access-grid">
              <div className="cxp-access-card">
                <h4>Test Cancellation Policy <span className="cxp-access-sub">(this config page)</span></h4>
                <table className="cxp-access-table">
                  <thead>
                    <tr><th>Role</th><th>View</th><th>Update</th></tr>
                  </thead>
                  <tbody>
                    {policyAccess.length === 0 ? (
                      <tr><td colSpan={3} className="cxp-access-empty">No role has been granted access yet.</td></tr>
                    ) : (
                      policyAccess.map((r) => (
                        <tr key={r.roleId}>
                          <td>{r.roleName}</td>
                          <td>{r.view ? '✅' : '—'}</td>
                          <td>{r.update ? '✅' : '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="cxp-access-card">
                <h4>Cancellation Approvals <span className="cxp-access-sub">(the escalation inbox)</span></h4>
                <table className="cxp-access-table">
                  <thead>
                    <tr><th>Role</th><th>View</th><th>Approve</th></tr>
                  </thead>
                  <tbody>
                    {approvalsAccess.length === 0 ? (
                      <tr><td colSpan={3} className="cxp-access-empty">No role has been granted access yet.</td></tr>
                    ) : (
                      approvalsAccess.map((r) => (
                        <tr key={r.roleId}>
                          <td>{r.roleName}</td>
                          <td>{r.view ? '✅' : '—'}</td>
                          <td>{r.approve ? '✅' : '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CancellationPolicyPage;
